import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table'
import { CheckCircle, ExternalLink, MinusCircle, Snowflake, XCircle } from 'lucide-react'
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { Badge } from 'slab'
import { getLocaleDateString } from 'utils'
import { useGetBrokersMembersMemberIdCards } from 'utils/web_api'
import { type BrokersCard, BrokersCardStatus, BrokersCardType } from 'utils/web_api.schemas'

type CardsTableProps = {
  memberId: string
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

const columnHelper = createColumnHelper<BrokersCard>()

const StatusBadge = ({ status }: { status: BrokersCard['status'] }) => {
  switch (status) {
    case BrokersCardStatus.Active:
      return <Badge type='success' leftIcon={CheckCircle}>Active</Badge>
    case BrokersCardStatus.Inactive:
      return <Badge type='neutral' leftIcon={MinusCircle}>Inactive</Badge>
    case BrokersCardStatus.Frozen:
      return <Badge type='warning' leftIcon={Snowflake}>Frozen</Badge>
    case BrokersCardStatus.ClosedByCustomer:
      return <Badge type='critical' leftIcon={XCircle}>Closed</Badge>
    case BrokersCardStatus.Stolen:
      return <Badge type='critical' leftIcon={XCircle}>Stolen</Badge>
    case BrokersCardStatus.Lost:
      return <Badge type='critical' leftIcon={XCircle}>Lost</Badge>
    default:
      return <Badge type='neutral'>{status}</Badge>
  }
}

const TypeBadge = ({ type }: { type: BrokersCard['type'] }) => {
  switch (type) {
    case BrokersCardType.IndividualVirtualHRACard:
      return <Badge type='info'>Virtual HRA</Badge>
    case BrokersCardType.IndividualPhysicalHRACard:
      return <Badge type='success'>Physical HRA</Badge>
    case BrokersCardType.IndividualVirtualDebitCard:
      return <Badge type='neutral'>Legacy Virtual</Badge>
    case BrokersCardType.IndividualPhysicalDebitCard:
      return <Badge type='warning'>Legacy Physical</Badge>
    default:
      return <Badge type='neutral'>{type}</Badge>
  }
}

const CardsTable = ({
  memberId,
  pagination,
  setPagination,
}: CardsTableProps) => {
  const { data, isLoading, isError, error } =
    useGetBrokersMembersMemberIdCards(memberId, {
      'page[number]': pagination.pageIndex,
      'page[size]': pagination.pageSize,
    }, {
      query: {
        enabled: !!memberId,
        refetchOnWindowFocus: false,
      },
    })

  const [paginationMetadata, setPaginationMetadata] = useState<{
    total_records: number
    current_page: number
    total_pages: number
    next_page: number | null
    prev_page: number | null
  }>()

  const columns = useMemo(
    () => [
      columnHelper.display({
        header: 'Actions',
        cell: ({ row }) => {
          const card = row.original
          if (!card.remote_link) {
            return null
          }
          return (
            <DisplayTooltipOnHover text='View card in dashboard'>
              <div
                className='cursor-pointer h-full'
                onClick={(e) => {
                  e.preventDefault()
                  window.open(card.remote_link, '_blank', 'noopener noreferrer')
                }}
              >
                <ExternalLink size={20} className='stroke-current text-gray-600' />
              </div>
            </DisplayTooltipOnHover>
          )
        },
      }),
      columnHelper.accessor('type', {
        header: 'Type',
        cell: (info) => <TypeBadge type={info.getValue()} />,
      }),
      columnHelper.accessor('last4_digits', {
        header: 'Last 4',
        cell: (info) => <Badge type='info'>{info.getValue()}</Badge>,
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => <StatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor('created_at', {
        header: 'Created',
        cell: (info) => getLocaleDateString(new Date(info.getValue())),
      }),
      columnHelper.accessor('updated_at', {
        header: 'Last Updated',
        cell: (info) => getLocaleDateString(new Date(info.getValue())),
      }),
    ],
    []
  )

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    state: {
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data.pagination)
    }
  }, [data?.pagination])

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      errorMessage={(error as GrapeAPIErrorType)?.errors[0]}
      noDataMessage='No cards found for this member.'
      paginationLabelSingular='card'
      paginationMetadata={paginationMetadata}
      tableMessageHeightClass='h-32'
      onNextPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.next_page || 0,
        })}
      onPreviousPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.prev_page || 0,
        })}
    />
  )
}

export default CardsTable
